import { Action, Reducer } from 'redux';
import { delay } from 'redux-saga/effects';
import { fork, call, takeEvery, put, all, select } from 'redux-saga/effects';

import { ITableConfig } from '../components/common/table';

import Api from '../api';

import { IRootState } from './index';
import Office from '../models/Office';

import { actionCreators as layoutActionCreators } from '../reducers/Layout';
import { selectors as authSelectors } from '../reducers/Auth';

import PagedResultSet from '../models/common/PagedResultSet';
import { ODataOptions } from '../util/ODataBuilder';
import IdentifyDto from '../models/IdentityDto';
import { replace } from 'connected-react-router';

// Store
export interface IOfficeState {
    isLoadingOffices: boolean;
    offices: Array<Office> | undefined;
    adminOfficesPageTableConfig: ITableConfig;
    officeLocation: Office;
}

const initialState: IOfficeState = {
    isLoadingOffices: false,
    offices: undefined,
    officeLocation: undefined,
    adminOfficesPageTableConfig: {
        rowCount: 20,
        pageNumber: 1,
        idField: 'id',
        columns: [
            { name: 'Name', mapping: 'name' },
            { name: 'Address Line 1', mapping: 'address.addressLine1' },
            { name: 'Address Line 2', mapping: 'address.addressLine2' },
            { name: 'City', mapping: 'address.city' },
            { name: 'Zipcode', mapping: 'address.zipCode' },
            { name: 'State', mapping: 'address.state.abbreviation' },
        ],
    },
};

// Action Types
export enum actionTypes {
    GET_OFFICES = '[office] GET_OFFICES',
    GET_OFFICES_SUCCESS = '[company] GET_OFFICES_SUCCESS',
    GET_OFFICE_BY_ID = '[office] GET_OFFICE_BY_ID',
    GET_OFFICE_BY_ID_SUCCESS = '[office] GET_OFFICE_BY_ID_SUCCESS',
    CREATE_OFFICE = '[office] CREATE_OFFICE',
    CREATE_OFFICE_SUCCESS = '[office] CREATE_OFFICE_SUCCESS',
    UPDATE_OFFICE = '[office UPDATE_OFFICE',
    UPDATE_OFFICE_SUCCESS = '[office] UPDATE_OFFICE_SUCCESS',
    DELETE_OFFICE = '[office] DELETE_OFFICE',
    DELETE_OFFICE_SUCCESS = '[office] DELETE_OFFICE_SUCCESS',
}

// Action Creator
export const actionCreators = {
    getOffices: () => {
        return { type: actionTypes.GET_OFFICES };
    },
    getById: (id: number) => {
        return { type: actionTypes.GET_OFFICE_BY_ID, payload: id };
    },
    getByIdSuccess: (office: Office) => {
        return { type: actionTypes.GET_OFFICE_BY_ID_SUCCESS, payload: office };
    },
    getOfficesSuccess: (offices: PagedResultSet<Office>) => {
        return { type: actionTypes.GET_OFFICES_SUCCESS, payload: offices };
    },
    createOffice: (office: Office) => {
        return { type: actionTypes.CREATE_OFFICE, payload: office };
    },
    createOfficeSuccess: (officeId: IdentifyDto) => {
        return { type: actionTypes.CREATE_OFFICE_SUCCESS, payload: officeId };
    },
    updateOffice: (office: Office) => {
        return { type: actionTypes.UPDATE_OFFICE, payload: office };
    },
    updateOfficeSuccess: (officeId: IdentifyDto) => {
        return { type: actionTypes.UPDATE_OFFICE_SUCCESS, payload: officeId };
    },
    deleteOffice: (id: number) => {
        return { type: actionTypes.DELETE_OFFICE, payload: id };
    },
    deleteOfficeSuccess: (officeId: IdentifyDto) => {
        return { type: actionTypes.DELETE_OFFICE_SUCCESS, payload: officeId };
    },
};

// Reducer
const reducer: Reducer<IOfficeState> = (state: IOfficeState = initialState, action: Action): IOfficeState => {
    switch (action.type) {
        case actionTypes.GET_OFFICES:
            return {
                ...state,
                offices: initialState.offices,
                isLoadingOffices: true,
            };
        case actionTypes.GET_OFFICES_SUCCESS:
            const getOfficesPayload = (action as { type: string; payload: Array<Office> }).payload;
            return {
                ...state,
                offices: getOfficesPayload,
                isLoadingOffices: false,
            };
        case actionTypes.GET_OFFICE_BY_ID:
            return {
                ...state,
                offices: initialState.offices,
                isLoadingOffices: false,
            };
        case actionTypes.GET_OFFICE_BY_ID_SUCCESS:
            const getOfficeByIdPayload = (action as { type: string; payload: Office }).payload;
            return {
                ...state,
                officeLocation: getOfficeByIdPayload,
                isLoadingOffices: false,
            };
        case actionTypes.DELETE_OFFICE:
            return {
                ...state,
                offices: state.offices,
                isLoadingOffices: false,
            };
        case actionTypes.DELETE_OFFICE_SUCCESS:
            const getDeletedOfficePayload = (action as { type: string; payload: number }).payload;
            return {
                ...state,
                offices: state.offices.filter((x) => x.id !== getDeletedOfficePayload),
                isLoadingOffices: false,
            };
        case actionTypes.UPDATE_OFFICE:
            return {
                ...state,
                offices: state.offices,
                isLoadingOffices: false,
            };
        case actionTypes.UPDATE_OFFICE_SUCCESS:
            return {
                ...state,
                offices: state.offices,
                isLoadingOffices: false,
            };
        default:
            return state;
    }
};

export default reducer;

// Selectors
const officesSelector = (state: IRootState): Array<Office> | undefined => state.office.offices;
const officeLocationSelector = (state: IRootState): Office => state.office.officeLocation;

export const selectors = {
    isLoadingOffices: (state: IRootState): boolean => state.office.isLoadingOffices || state.layout.initialLoading,
    officeLocation: officeLocationSelector,
    offices: officesSelector,
    adminOfficesPageTableConfig: (state: IRootState): ITableConfig => state.office.adminOfficesPageTableConfig,
};

// Side-Effects
const sagas = {
    getOffices: function* (action: { type: string }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const tableConfig = yield select(selectors.adminOfficesPageTableConfig);
        const options = ODataOptions.from(tableConfig);
        const [result] = yield all([call(Api.Office.getAll, options, jwt), delay(200)]);
        yield put(actionCreators.getOfficesSuccess(result));
    },
    getById: function* (action: { type: string; payload: number }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([call(Api.Office.getById, action.payload, jwt), delay(200)]);
        yield put(actionCreators.getByIdSuccess(result));
    },
    createOffice: function* (action: { type: string; payload: Office }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        // action.payload.id = 0;
        const [result] = yield all([call(Api.Office.create, action.payload, jwt), delay(200)]);
        yield put(actionCreators.createOfficeSuccess(result));

        yield put(replace('/admin/offices'));
    },
    updateOffice: function* (action: { type: string; payload: Office }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([call(Api.Office.update, action.payload, jwt), delay(200)]);
        yield put(actionCreators.updateOfficeSuccess(result));
        yield put(replace('/admin/offices'));
    },
    deleteOffice: function* (action: { type: string; payload: number }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([call(Api.Office.delete, action.payload, jwt), delay(200)]);
        yield put(actionCreators.deleteOfficeSuccess(result));
        yield put(replace('/admin/offices'));
    },
};

const watches = {
    getOffices: function* () {
        yield takeEvery(actionTypes.GET_OFFICES, sagas.getOffices);
    },
    getById: function* () {
        yield takeEvery(actionTypes.GET_OFFICE_BY_ID, sagas.getById);
    },
    createOffice: function* () {
        yield takeEvery(actionTypes.CREATE_OFFICE, sagas.createOffice);
    },
    updateOffice: function* () {
        yield takeEvery(actionTypes.UPDATE_OFFICE, sagas.updateOffice);
    },
    deleteOffice: function* () {
        yield takeEvery(actionTypes.DELETE_OFFICE, sagas.deleteOffice);
    },
};

export function* saga() {
    yield fork(watches.getById);
    yield fork(watches.getOffices);
    yield fork(watches.createOffice);
    yield fork(watches.updateOffice);
    yield fork(watches.deleteOffice);
}



// WEBPACK FOOTER //
// ./src/reducers/Office.ts